import { useCallback } from 'react';
import { addMatrixIdForMessageTip } from '../../../../../shared/actions/matrixAction';
import { captureChatException } from 'shared/sentry';

/**
 * Returns a stable callback to handle successful tip events from chat.
 * Mirrors the inline implementation previously defined in ChattingPage.
 */
export const useHandleTipSuccessInChat = ({
  sendTextMessage,
  scrollToBottom,
  openTipModalRef,
  forceScrollToBottomRef,
}) =>
  useCallback(
    (tipMessage, tipMeta) => {
      // Prefer structured payload when available (message tips from TipModal).
      if (tipMeta && sendTextMessage) {
        const {
          message,
          isFirstMessageTip,
          creditAmount,
          amount,
          vatAmount,
          vatPercentage,
          baseAmount,
          realPrice,
          extraCharge,
          platformFee,
          tip_type,
          tipId,
        } = tipMeta;

        const payload = {
          message: typeof message === 'string' && message.trim() ? message.trim() : null,
          isFirstMessageTip: Boolean(isFirstMessageTip),
          creditAmount: creditAmount ?? null,
          realPrice: realPrice ?? null,
          amount: amount ?? null,
          vatAmount: vatAmount ?? null,
          vatPercentage: vatPercentage ?? null,
          baseAmount: baseAmount ?? null,
          extraCharge: extraCharge ?? null,
          platformFee: platformFee ?? null,
          tip_type: tip_type || 'messages',
          tipId: tipId ?? null,
          isRefunded: false,
          isExtended: false,
        };

        // Clear pending composer state if we previously staged a message for tip.
        if (openTipModalRef.current) {
          delete openTipModalRef.current.pendingMessageWithTip;
          openTipModalRef.current?.clearComposerMessage?.();
        }

        const hasRealPrice =
          realPrice !== null &&
          realPrice !== undefined &&
          !(typeof realPrice === 'string' && !realPrice.trim());
        if (!hasRealPrice) return;

        sendTextMessage(JSON.stringify(payload)).then((result) => {
          const matrixEventId = result?.eventId || null;
          const tipId = payload.tipId ?? null;

          if (matrixEventId && tipId) {
            addMatrixIdForMessageTip({
              matrixEventId: matrixEventId,
              tipId: tipId,
            }).catch((error) => {
              captureChatException(error, {
                tags: { feature: 'chat', action: 'tip-matrix-id' },
              });
            });
          }

          if (forceScrollToBottomRef) {
            forceScrollToBottomRef.current = true;
          }
          scrollToBottom?.('smooth');
        });
        return;
      }

      // Legacy fallback: derive "+$XX tipped" line from the success message.
      const match = String(tipMessage || '').match(/\$([\d.]+)/);
      const amount = match ? match[1] : null;
      if (amount == null || !sendTextMessage) return;

      const formatted =
        Number(amount) === parseInt(amount, 10)
          ? String(parseInt(amount, 10))
          : Number(amount).toFixed(2);
      const tipLine = `+$${formatted} tipped`;
      const pendingMessage = openTipModalRef.current?.pendingMessageWithTip;

      if (typeof pendingMessage === 'string' && pendingMessage.trim()) {
        delete openTipModalRef.current.pendingMessageWithTip;
        openTipModalRef.current?.clearComposerMessage?.();
        sendTextMessage(`${pendingMessage.trim()}\n${tipLine}`).then(() => {
          if (forceScrollToBottomRef) {
            forceScrollToBottomRef.current = true;
          }
          scrollToBottom?.('smooth');
        });
      } else {
        sendTextMessage(tipLine).then(() => {
          if (forceScrollToBottomRef) {
            forceScrollToBottomRef.current = true;
          }
          scrollToBottom?.('smooth');
        });
      }
    },
    [sendTextMessage, scrollToBottom, openTipModalRef, forceScrollToBottomRef]
  );
